import type {
  UserAddress,
  UserProfile,
} from '@autobid/strapi-integration/typescript/webapi/User'
import type { FormKitSchemaCondition, FormKitSchemaFormKit, FormKitSchemaNode } from '@formkit/core'
import type { MaybeRef } from 'vue'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { addCaptcha } from '@autobid/ui/utils/captcha/addCaptcha'
import { useProfile } from './useProfile'

interface FormKitSchemaFactoryProps {
  schema: FormKitSchemaFormKit[]
  captcha?: boolean
  onGetDefaults?: (data: Record<string, string>) => unknown
}

interface FieldWithDefaultValue {
  name: string
  defaultValue: string
  defaultValueProps?: Record<string, unknown>
}

function getNestedValue(data: unknown, path: string) {
  const PATH_SEPARATOR = '.'

  let value: unknown = data
  const keys = path.split(PATH_SEPARATOR)

  for (const key of keys) {
    if (
      !value
      || (value && typeof value === 'object' && value[key as keyof typeof value] === undefined)
    ) {
      // key doesn't exist
      value = null
      break
    }

    value = value[key as keyof typeof value]
  }

  return value
}

/**
 * @description The data in database is not normalized. The address field contains the street and house no, it should be splitted.
 */
function appendSplittedAddress(address: UserAddress) {
  if (!address)
    return address

  // eslint-disable-next-line regexp/no-misleading-capturing-group, regexp/optimal-quantifier-concatenation
  const REGEX = /(\D+)\s?(.+)/
  const matches = address.address.match(REGEX)
  const houseNumber = matches ? matches[matches.length - 1] : ''
  const street = address.address.replace(houseNumber, '')

  return {
    ...address,
    houseNumber,
    street,
  }
}

function getFieldsWithDefaultValue(schema: FormKitSchemaCondition | FormKitSchemaFormKit[]) {
  const loopOverElements = (
    data: FormKitSchemaCondition | FormKitSchemaFormKit[] | FormKitSchemaNode[],
  ) => {
    let result: Array<FormKitSchemaFormKit | FormKitSchemaFormKit[]> = []

    if (!Array.isArray(data))
      return []

    for (const element of data) {
      if (typeof element === 'string')
        continue

      if ('$formkit' in element && 'defaultValue' in element) {
        result.push(element)
      }
      else if (
        'children' in element
        && typeof element.children !== 'string'
        && element.children
      ) {
        result = [...result, loopOverElements(element.children)]
      }
    }

    return result.flat()
  }

  return loopOverElements(schema) as unknown as FieldWithDefaultValue[]
}

export async function getDefaultValuesFromProfile(fields: FieldWithDefaultValue[], profile: MaybeRef<UserProfile | undefined | null>) {
  try {
    while (!unref(profile)) {
      await new Promise(resolve => setTimeout(resolve, 1000))
    }

    const unrefedProfile = unref(profile)

    if (!unrefedProfile?.extendedData) {
      return {}
    }

    return fields.reduce((acc: Record<string, unknown>, field) => {
      let pathToValue = field.defaultValue.replace('profile:', '')
      if (pathToValue.startsWith('extendedData.addressBook.')) {
        pathToValue = pathToValue.replace('extendedData.addressBook.', '')
        const { defaultItem, items }
          = unrefedProfile.extendedData?.addressBook ?? {}

        if (!items)
          return acc

        // get the default address or the first in the object
        const address = appendSplittedAddress(
          defaultItem && items[defaultItem] ? items[defaultItem] : Object.values(items)[0],
        )

        acc[field.name] = getNestedValue(address, pathToValue)
        return acc
      }

      const defaultValueProps = field?.defaultValueProps as Record<string, any>
      const allowedContactTypes = defaultValueProps?.allowedContactTypes

      if (!allowedContactTypes) {
        acc[field.name] = getNestedValue(unref(profile), pathToValue)
        return acc
      }

      const contactType = defaultValueProps.contactType
      if (
        Array.isArray(allowedContactTypes)
        && allowedContactTypes.includes(contactType)
      ) {
        acc[field.name] = getNestedValue(unref(profile), pathToValue)
      }

      return acc
    }, {})
  }
  catch (error) {
    console.error(error)
    return {}
  }
}

function groupFieldsWithDefaultValue(fields: FieldWithDefaultValue[]) {
  const result: Record<string, FieldWithDefaultValue[]> = {}

  fields.forEach((field) => {
    if (!field.defaultValue) {
      return
    }

    const [group, pathToValue] = field.defaultValue.split(':')

    if (!result[group]) {
      result[group] = []
    }

    result[group].push({
      ...field,
      defaultValue: pathToValue,
    })
  })

  return result
}

export async function getDefaultValues(schema: FormKitSchemaCondition | FormKitSchemaFormKit[], profile: MaybeRef<UserProfile | undefined | null>) {
  const fields = getFieldsWithDefaultValue(schema)
  const groupedFields = groupFieldsWithDefaultValue(fields)
  let result: Record<string, string> = {}

  if (groupedFields.profile) {
    const defaultsFromProfile = Object.entries(await getDefaultValuesFromProfile(groupedFields.profile, profile)).reduce(
      (acc: Record<string, string>, el) => {
        const [key, value] = el

        acc[key] = String(value ?? '')

        return acc
      },
      {},
    )

    result = {
      ...result,
      ...defaultsFromProfile,
    }
  }

  return result
}

export function useFormKitSchemaFactory() {
  const { isAuthed } = useAutobidAuth()
  const { data: profile } = useProfile()

  const init = async (props: FormKitSchemaFactoryProps) => {
    let schema = [...props.schema]

    if (props.captcha) {
      schema = addCaptcha(schema)
    }

    function fetchOptions(schema: FormKitSchemaFormKit[]): Promise<FormKitSchemaFormKit[]>
    function fetchOptions(schema: FormKitSchemaNode[]): Promise<FormKitSchemaNode[]>
    function fetchOptions(
      schema: FormKitSchemaFormKit[] | FormKitSchemaNode[],
    ): Promise<FormKitSchemaFormKit[] | FormKitSchemaNode[]> {
      return Promise.all(
        schema.map(async (element) => {
          if (typeof element === 'string') {
            return element
          }

          const newElement = { ...element }

          if ('children' in newElement && Array.isArray(newElement.children)) {
            newElement.children = await fetchOptions(newElement.children)
          }

          return newElement
        }),
      )
    }

    if (isAuthed.value) {
      schema = await fetchOptions(schema)
    }

    if (isAuthed.value && props.onGetDefaults) {
      const defaults = await getDefaultValues(schema, profile)

      props.onGetDefaults(defaults)
    }

    return { schema }
  }

  return { init }
}
